// Client-side ports of the district logic that used to live in the CMS AppSync
// resolvers (packages/cms/cdk/lambda/resolvers/district-overrides.ts +
// cms-ms-hat-display.ts `matchDistrictPrograms`). The static publish pipeline
// ships hat index/detail/region files WITHOUT district overrides, so we apply
// them here from the per-district `district-settings/<id>.json` artifact.

import type { CMSMSDistrictProgram } from "../types/cms-types";
import type {
  DistrictSettingsOverride,
  DistrictSettingsProgram,
  OverridesByPathway,
} from "../types/districtSettings";

const idOf = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "object" && "id" in (value as Record<string, unknown>)) {
    const id = (value as Record<string, unknown>).id;
    return id == null ? null : String(id);
  }
  return null;
};

/** Build the resolver-style override map from the published override list. */
export const buildOverridesByPathway = (
  overrides: DistrictSettingsOverride[] | undefined,
): OverridesByPathway => {
  const out: OverridesByPathway = new Map();
  for (const o of overrides ?? []) {
    if (!o.pathwayId) continue;
    out.set(o.pathwayId, {
      add: new Set(o.addHats ?? []),
      remove: new Set(o.removeHats ?? []),
      pathway: o.pathway,
    });
  }
  return out;
};

/**
 * Apply overrides to a hat-index entry whose `msPathway`/`msCareerCluster` are
 * arrays of id strings. Port of the resolver's `applyOverridesToHatIndex`:
 * addHats pushes the pathway id + unions its cluster ids; removeHats drops the
 * pathway id only (clusters are never subtracted).
 */
export const applyOverridesToHatIndexEntry = <
  T extends {
    id: string | number;
    msPathway?: string[] | null;
    msCareerCluster?: string[] | null;
  },
>(
  hat: T,
  overrides: OverridesByPathway,
): T => {
  if (overrides.size === 0) return hat;
  const hatId = String(hat.id);
  const pathways = new Set<string>((hat.msPathway || []).map(String));
  const clusters = new Set<string>((hat.msCareerCluster || []).map(String));
  let changed = false;
  for (const [pathwayId, { add, remove, pathway }] of overrides) {
    if (add.has(hatId) && !pathways.has(pathwayId)) {
      pathways.add(pathwayId);
      changed = true;
      for (const c of pathway.careerCluster ?? []) {
        if (!clusters.has(c.id)) clusters.add(c.id);
      }
    }
    if (remove.has(hatId) && pathways.has(pathwayId)) {
      pathways.delete(pathwayId);
      changed = true;
    }
  }
  if (!changed) return hat;
  return {
    ...hat,
    msPathway: Array.from(pathways),
    msCareerCluster: Array.from(clusters),
  };
};

/**
 * Apply overrides to a hat whose `msPathway`/`msCareerCluster` are arrays of
 * objects (or ids). Port of the resolver's `applyOverridesToHatFull`: addHats
 * pushes the override's stored pathway object + its cluster objects; removeHats
 * filters the pathway by id; clusters are never subtracted.
 */
export const applyOverridesToHatFull = <
  T extends {
    id: string | number;
    msPathway?: unknown[] | null;
    msCareerCluster?: unknown[] | null;
  },
>(
  hat: T,
  overrides: OverridesByPathway,
): T => {
  if (overrides.size === 0) return hat;
  const hatId = String(hat.id);
  const list = [...(hat.msPathway || [])];
  const presentIds = new Set<string>();
  for (const item of list) {
    const id = idOf(item);
    if (id) presentIds.add(id);
  }

  const clusterList = [...(hat.msCareerCluster || [])];
  const presentClusterIds = new Set<string>();
  for (const item of clusterList) {
    const id = idOf(item);
    if (id) presentClusterIds.add(id);
  }

  let changed = false;
  for (const [pathwayId, { add, pathway }] of overrides) {
    if (add.has(hatId) && !presentIds.has(pathwayId)) {
      list.push(pathway ?? { id: pathwayId });
      presentIds.add(pathwayId);
      changed = true;
      for (const cluster of pathway.careerCluster ?? []) {
        if (cluster.id && !presentClusterIds.has(cluster.id)) {
          clusterList.push(cluster);
          presentClusterIds.add(cluster.id);
        }
      }
    }
  }
  let filtered = list;
  for (const [pathwayId, { remove }] of overrides) {
    if (remove.has(hatId) && presentIds.has(pathwayId)) {
      filtered = filtered.filter((item) => idOf(item) !== pathwayId);
      changed = true;
    }
  }
  if (!changed) return hat;
  return { ...hat, msPathway: filtered, msCareerCluster: clusterList };
};

/**
 * Match a hat to the district's programs. Port of the resolver's
 * `matchDistrictPrograms`, but feeder filtering reads `feederMiddleSchoolIds`
 * pre-joined onto each program school (no separate high-school lookup).
 */
export const matchDistrictProgramsClient = (
  programs: DistrictSettingsProgram[],
  hatId: string,
  hatPathwayIds: Set<string>,
  middleSchoolId?: string,
): CMSMSDistrictProgram[] => {
  return programs
    .map((program): CMSMSDistrictProgram | null => {
      let matchType = "NOT_A_MATCH";
      let matchInfo: string | null = null;

      const hatOverride = program.hatOverrides.find((o) => o.hatId === hatId);
      if (hatOverride) {
        matchType = hatOverride.matchType;
        matchInfo = "Direct hat match";
      } else {
        const matching = program.pathwayMatches.filter((m) =>
          hatPathwayIds.has(m.pathwayId),
        );
        if (matching.length > 0) {
          matchType = matching.some((m) => m.matchType === "BEST_MATCH")
            ? "BEST_MATCH"
            : "CLOSE_MATCH";
          const names = matching.map((m) => m.pathwayName).filter(Boolean);
          matchInfo =
            names.length > 0 ? `Via pathway: ${names.join(", ")}` : null;
        }
      }

      if (matchType === "NOT_A_MATCH") return null;

      const allSchools = program.schools.map((s) => ({
        id: s.id,
        name: s.name,
        matchType: s.matchType,
      }));

      let schools = allSchools;
      let schoolMatchType: "feeder" | "all" = "all";
      if (middleSchoolId) {
        const feeders = program.schools.filter((s) =>
          s.feederMiddleSchoolIds.includes(middleSchoolId),
        );
        if (feeders.length > 0) {
          schools = feeders.map((s) => ({
            id: s.id,
            name: s.name,
            matchType: s.matchType,
          }));
          schoolMatchType = "feeder";
        }
      }

      return {
        id: program.id,
        name: program.name,
        matchType,
        matchInfo: matchInfo ?? undefined,
        schoolMatchType,
        schools,
      };
    })
    .filter((p): p is CMSMSDistrictProgram => p !== null);
};
